import React from 'react';
import { connect } from 'react-redux';
import { withTranslation } from 'react-i18next';
import { List, SendHorizontal, Undo2, Briefcase } from 'lucide-react';
import { IAppState } from '../redux/store/types';
import {
  updateRecordFieldAction,
  setRecordFormErrorsAction,
  resetRecordFormAction,
  prepareCreateRecordCommandAction,
  prepareUpdateRecordCommandAction,
  addRecordAction,
  setRecordStateAction,
  deleteRecordAndReadNextAction,
  setViewModeAction,
  setShowAllLinkableAction,
  resetRecordsAction,
  prepareReadRecordsCommandAction,
} from '../redux/records/actions';
import { actions as summariesActions } from '../redux/summaries/slice';
import { bindActionCreators, Dispatch } from 'redux';
import initialState from '../redux/store/initialState';
import { v4 as uuidv4 } from 'uuid';
import { ILLMProfile } from 'redux/llmProfiles/types';
import { ILatestPublicRecord, IRecord, IRecordFormData, ViewMode } from '../redux/records/types';
import { UserType } from 'redux/auth/types';
import { areArraysShallowEqual, USER_TEXT_MAX_LENGTH } from 'utils/utils';
import { toast } from 'react-toastify';
import { FileUploadButton } from './FileUploadButton';
import { SaasTenantSelector } from './SaasTenantSelector';
import { RecordAccessLevel, TESTING_ALLOWED_USER_IDS, TESTING_SIMULATED_LLM_DELAY_MS, TESTING_USER_TEXT_PREFIX } from 'data-types';

/**
 * A form component for creating and editing records.
 * Handles both creation of new records and modification of existing ones,
 * including the ability to enable/disable records in edit mode.
 */
class RecordFormBase extends React.Component<IRecordFormProps> {
  private initialFormValues: typeof initialState.records.recordForm;

  constructor(props: IRecordFormProps) {
    super(props);

    // Initialize with default values
    this.initialFormValues = { ...initialState.records.recordForm };
  }

  /**
   * Initialize initialFormValues when component mounts
   */
  componentDidMount() {
    this.updateInitialFormValues();
  }

  /**
   * Update initialFormValues when component updates
   */
  componentDidUpdate(prevProps: IRecordFormProps) {
    // Update initialFormValues if formData changed (e.g., editing different record)
    if (prevProps.formData.id !== this.props.formData.id) {
      this.updateInitialFormValues();
    }
  }

  /**
   * Helper method to update initialFormValues based on current props
   */
  private updateInitialFormValues() {
    const { formData, llmProfiles, userType } = this.props;

    if (!formData.id) {
      // create mode
      const initialProfileId = (userType === UserType.FFU ? llmProfiles?.find((p) => p.level !== 'PRO') : llmProfiles[0])?.profile_id || '';
      this.initialFormValues = { ...initialState.records.recordForm, llm_profile_id: initialProfileId };
      formData.llm_profile_id = initialProfileId;
    } else {
      // edit mode
      this.initialFormValues = { ...formData };
      this.forceUpdate();
    }
  }

  /**
   * Renders the form component.
   * @returns The JSX element representing the form
   */
  render() {
    const { t, formData, userType } = this.props;
    const mode = formData.id ? 'edit' : 'create';
    const { user_text, llm_profile_id, access_level, retainCurrentAccessLevel, is_archived, errors } = formData;

    return (
      <div className={`table-row record input ${(mode === 'edit' && 'update') || ''} ${(this.hasUnsavedChanges() && 'has-changes') || ''}`}>
        <div className='required input-and-error-container'>
          <div className='textarea-container'>
            <textarea
              rows={4}
              value={user_text}
              onChange={(e) => this.props.updateRecordFieldAction('user_text', e.target.value)}
              className={`user-text ${errors.user_text ? 'error-message' : ''}`}
              disabled={mode === 'edit'}
              placeholder={t('records.askMeAnything')}
            />

            {mode === 'edit' && <FileUploadButton JWT={this.props.JWT} recordId={formData.id} onTextualFilesChange={this.handleFilesChange} />}
          </div>

          {errors.user_text && <span className='error-message'>{errors.user_text}</span>}
        </div>

        <div className='footer'>
          <div className='llm-profile-selector'>
            <select value={llm_profile_id} onChange={(e) => this.props.updateRecordFieldAction('llm_profile_id', e.target.value)}>
              {userType === UserType.Admin && (
                <option key='*' value='*'>
                  *
                </option>
              )}

              {this.props.llmProfiles
                .filter((profile) => !profile.level || userType === UserType.Admin || userType === UserType.SaasTenant)
                .map((profile) => (
                  <option key={profile.profile_id} value={profile.profile_id}>
                    {profile.model_id}
                  </option>
                ))}
            </select>
          </div>

          <div className='access-level-selector' title={(retainCurrentAccessLevel && t('records.linkedByAnotherUser')) || ''}>
            <select
              value={access_level}
              onChange={(e) => this.props.updateRecordFieldAction('access_level', parseInt(e.target.value, 10))}
              disabled={retainCurrentAccessLevel}
            >
              <option value={RecordAccessLevel.Private}>{t('common.private')}</option>
              <option value={RecordAccessLevel.Restricted}>{t('common.restricted')}</option>
              <option value={RecordAccessLevel.Public}>{t('common.public')}</option>
            </select>
          </div>

          {access_level === RecordAccessLevel.Restricted && (
            <div className='saas-tenant-selector-container'>
              <SaasTenantSelector
                selectedSaasTenantIds={formData.shareWithSaasTenantIds || []}
                sharedSaasTenantIdsAndNames={this.getSharedSaasTenantIdsAndNames(mode)}
                onSelectionChange={this.handleSaasTenantSelectionChange}
              />
            </div>
          )}

          {mode === 'edit' && (
            <div className='archived-checkbox'>
              <label>
                <input type='checkbox' checked={is_archived} onChange={(e) => this.props.updateRecordFieldAction('is_archived', e.target.checked)} />
                {t('records.archived')}
              </label>
            </div>
          )}

          <div className='actions'>
            {mode === 'create' && (
              <button onClick={this.handleJobDescriptionTemplate} className='action-button job-description' title={t('jobDescription.title')}>
                <Briefcase />
              </button>
            )}

            {this.hasUnsavedChanges() && (
              <button onClick={this.handleSubmit} className='action-button save' title={t('common.send')}>
                <SendHorizontal />
              </button>
            )}

            <button
              onClick={this.handleCancel}
              className={`action-button cancel ${(this.props.latestPublicRecords.length > 0 && 'latest-records-displayed') || ''}`}
              title={t(formData.user_text ? 'common.cancel' : 'records.showExistingChats')}
            >
              {formData.user_text ? <Undo2 /> : <List />}
            </button>
          </div>
        </div>
      </div>
    );
  }

  /**
   * Handles SaaS tenant selection changes
   */
  handleSaasTenantSelectionChange = (selectedIds: string[]) => {
    this.props.updateRecordFieldAction('shareWithSaasTenantIds', selectedIds);
  };

  /**
   * Returns the currently-shared SaaS tenants (id + display name) for a record being edited.
   * @param mode - Form mode; only returns data in 'edit' mode.
   * @returns Array of `{ saas_tenant_id, name }` or `undefined` when not editing / data not present.
   */
  private getSharedSaasTenantIdsAndNames(mode: 'edit' | 'create'): { saas_tenant_id: string; name: string }[] | undefined {
    const { formData, records } = this.props;
    if (mode !== 'edit') return undefined;

    const editedRecord = records.find((r) => r.record_id === formData.id);
    if (!editedRecord?.sharedSaasTenantIdsAndNames) return undefined;
    return editedRecord.sharedSaasTenantIdsAndNames.map((t) => ({ saas_tenant_id: t.id, name: t.name || t.id }));
  }

  /**
   * Handles file upload changes
   */
  handleFilesChange = (fileKeys: string[]) => {
    this.props.updateRecordFieldAction('uploadedFiles', fileKeys);
  };

  /**
   * Handles job description template insertion
   */
  handleJobDescriptionTemplate = () => {
    const { t } = this.props;
    const template = t('jobDescription.template');

    // Put the instruction at the beginning so it's immediately visible
    const templateWithPlaceholder = `[PASTE YOUR JOB DESCRIPTION BELOW]\n\n${template}`;
    this.props.updateRecordFieldAction('user_text', templateWithPlaceholder);

    // Set LLM profile to Claude 3.0 Haiku
    // const claude3Profile = this.props.llmProfiles.find(profile => profile.model_id?.includes('claude-3-haiku'));
    // if (claude3Profile) this.props.updateRecordFieldAction('llm_profile_id', claude3Profile.profile_id);
  };

  /**
   * Submits the current form values
   */
  handleSubmit = () => {
    const { formData, userType, userId, t } = this.props;
    const mode = formData.id ? 'edit' : 'create';

    const errors = this.validateForm(formData);

    if (Object.keys(errors).length === 0) {
      if (mode === 'create') {
        // Note: This is UI-only check. Server validates admin permissions.
        if ((userType === UserType.Admin || TESTING_ALLOWED_USER_IDS.includes(userId as string)) && formData.user_text.startsWith(TESTING_USER_TEXT_PREFIX))
          this.generateTestCommands(formData);
        else if (this.props.userRemainingCredit <= 0) toast.error(t('tokenUsage.noRemainingCredit'), { autoClose: false });
        else {
          // Normal single record creation
          if (this.props.showAllLinkable) {
            this.props.resetRecordsAction();
            this.props.setShowAllLinkableAction(false); // after adding a new record, show and focus to it.
          }

          const record_id = uuidv4();
          this.props.prepareCreateRecordCommandAction(
            record_id,
            formData.user_text,
            undefined,
            formData.llm_profile_id,
            formData.access_level,
            formData.shareWithSaasTenantIds,
          );
          this.props.addRecordAction({
            ...formData,
            record_id,
            ai_text: '',
            onroute: true,
          });
          this.props.resetRecordFormAction();
          this.props.clearSummariesAction();
          this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
        }
      } else {
        if (formData.is_archived) this.props.deleteRecordAndReadNextAction(formData.id);
        else this.props.setRecordStateAction({ ...formData, record_id: formData.id, onroute: true });
        const { errors, ...formDataWithoutErrors } = formData;
        this.props.prepareUpdateRecordCommandAction(formData.id, { ...formDataWithoutErrors });
        this.props.resetRecordFormAction();
        this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
      }
    } else {
      this.props.setRecordFormErrorsAction(errors);
    }
  };

  /**
   * --testing--Java spring boot,dotnet core,AWS,AWS ECS,AWS EKS,AWS EventBridge
   * --testing--NBA,Federrer,Nadal
   * --testing--U2,Coldplay,Disturbed
   * --testing--query:Explain--AWS,Java,React
   * --testing--iterations:40--AWS                  -    50 requests, in batches of 1,  pausing TESTING_SIMULATED_LLM_DELAY_MS between batches.
   * --testing--concurrent:40|iterations:25--AWS    - 1,000 requests, in batches of 40, pausing TESTING_SIMULATED_LLM_DELAY_MS between batches.
   */
  generateTestCommands = (values: { user_text: string; llm_profile_id: string; access_level: RecordAccessLevel; shareWithSaasTenantIds?: string[] }) => {
    const testPhrase = TESTING_USER_TEXT_PREFIX;

    // Skip if neither phrase is found
    if (!values.user_text.startsWith(testPhrase)) return;

    const testIndex = values.user_text.indexOf(testPhrase);
    const sentencesText = values.user_text.substring(testIndex + testPhrase.length).trim();

    // Extract iterations if specified with iterations: prefix
    let iterations = 1;
    let concurrent = 1;
    let remainingText = sentencesText;

    const parsePositiveInt = (value: string) => {
      const parsed = parseInt(value, 10);
      return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
    };

    // New format: concurrent:<n>|iterations:<m>--...
    // Also allow iterations:<m>|concurrent:<n>--...
    const concurrentIterationsMatch = sentencesText.match(/^(concurrent:(\d+)\|iterations:(\d+)|iterations:(\d+)\|concurrent:(\d+))--(.+)$/);
    if (concurrentIterationsMatch) {
      const concurrentValue = concurrentIterationsMatch[2] || concurrentIterationsMatch[5];
      const iterationsValue = concurrentIterationsMatch[3] || concurrentIterationsMatch[4];
      const parsedConcurrent = parsePositiveInt(concurrentValue);
      const parsedIterations = parsePositiveInt(iterationsValue);
      if (parsedConcurrent) concurrent = parsedConcurrent;
      if (parsedIterations) iterations = parsedIterations;
      remainingText = concurrentIterationsMatch[6].trim();
    }

    const iterationsMatch = sentencesText.match(/^iterations:(\d+)--(.+)$/);
    if (iterationsMatch && !concurrentIterationsMatch) {
      const parsedIterations = parsePositiveInt(iterationsMatch[1]);
      if (parsedIterations) iterations = parsedIterations;
      remainingText = iterationsMatch[2].trim();
    }

    // Extract custom query if specified with query: prefix
    let customQuery = 'Summarize, compare with similars, and demonstrate using a simple use case';
    let topicsText = remainingText; // e.g.: U2,Coldplay,Disturbed

    const queryMatch = remainingText.match(/^query:([^-]+)--(.+)$/);
    if (queryMatch) {
      customQuery = queryMatch[1].trim();
      topicsText = queryMatch[2].trim();
    }

    const sentences = topicsText
      .split(',')
      .map((s) => `${customQuery} ${s.trim()}`)
      .filter((s) => s.length > 0);

    const BATCH_PAUSE_MS = TESTING_SIMULATED_LLM_DELAY_MS + 10000; // extra buffer to let the backend complete the simulation. TODO: Optimize.
    const totalCommands = iterations * concurrent;
    const safeSentences = sentences.length > 0 ? sentences : [customQuery];

    /**
     * Sends one burst of requests.
     * @param batchStartIndex - 0-based index of the first command to send in this batch.
     * The range sent by a batch is: [batchStartIndex, batchEndIndexExclusive)
     * i.e. the start is inclusive and the end is exclusive.
     */
    const sendBatch = (batchStartIndex: number) => {
      // Exclusive upper bound for this batch (standard JS loop pattern).
      // Example: start=0, concurrent=40 => endExclusive=40 => indices 0..39 (40 items)
      const batchEndIndexExclusive = Math.min(batchStartIndex + concurrent, totalCommands);

      for (let commandIndex = batchStartIndex; commandIndex < batchEndIndexExclusive; commandIndex++) {
        const delayMs = (commandIndex - batchStartIndex) * 100;
        setTimeout(() => {
          const sentence = safeSentences[commandIndex % safeSentences.length];
          const record_id = uuidv4();
          this.props.addRecordAction({
            ...values,
            user_text: sentence,
            record_id,
            ai_text: '',
            onroute: true,
          });
          this.props.prepareCreateRecordCommandAction(
            record_id,
            `${TESTING_USER_TEXT_PREFIX}${sentence}`,
            undefined,
            values.llm_profile_id,
            values.access_level,
            values.shareWithSaasTenantIds,
          );
        }, delayMs);
      }

      if (batchEndIndexExclusive >= totalCommands) return;
      // Intentionally start the next batch a few seconds after BATCH_PAUSE_MS (plus the intra-batch dispatch spread)
      // to give the backend extra time to complete processing and avoid accumulating requests that could exceed the
      // Lambda ReservedConcurrentExecutions limit.
      const batchDispatchSpreadMs = Math.max(0, (batchEndIndexExclusive - batchStartIndex - 1) * 100);
      setTimeout(() => sendBatch(batchEndIndexExclusive), BATCH_PAUSE_MS + batchDispatchSpreadMs);
    };

    // Kick off the first batch immediately.
    sendBatch(0);

    this.props.resetRecordFormAction();
    this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
  };

  /**
   * Handles the cancel action with confirmation if there are unsaved changes
   */
  handleCancel = () => {
    let resetForm = !this.hasUnsavedChanges();
    if (!resetForm && window.confirm(this.props.t('common.confirmUnsavedChanges'))) resetForm = true;

    if (resetForm) {
      this.props.resetRecordFormAction();
      if (this.props.records.length === 0 && !this.props.isInitialLoadInProgress)
        this.props.prepareReadRecordsCommandAction({ readAllLinkable: this.props.showAllLinkable, searchText: this.props.searchText });
      this.props.setViewModeAction(ViewMode.SHOW_CONDENSED_RECORDS_VIEW);
    }
  };

  /**
   * Checks if the form has any unsaved changes by comparing current state with initial values
   */
  hasUnsavedChanges = (): boolean => {
    const { formData } = this.props;

    return (
      !!formData.user_text &&
      (formData.user_text !== this.initialFormValues.user_text ||
        formData.llm_profile_id !== this.initialFormValues.llm_profile_id ||
        formData.access_level !== this.initialFormValues.access_level ||
        formData.is_archived !== this.initialFormValues.is_archived ||
        !areArraysShallowEqual(formData.shareWithSaasTenantIds, this.initialFormValues.shareWithSaasTenantIds))
    );
  };

  /**
   * Validates record form fields
   * @param {object} values - Form values to validate
   * @returns {Record<string, string>} Validation errors if any
   */
  validateForm = (values: { user_text: string }, fieldToValidate?: keyof typeof values) => {
    const { t } = this.props;
    const errors: Record<string, string> = {};

    const validateSingleField = (field: keyof typeof values) => {
      switch (field) {
        case 'user_text':
          if (!values.user_text) errors.user_text = t('validation.required');
          else if (values.user_text.length > USER_TEXT_MAX_LENGTH) errors.user_text = t('validation.valueTooLong');
          break;
        default:
          break;
      }
    };

    if (fieldToValidate) validateSingleField(fieldToValidate);
    else validateSingleField('user_text');

    return errors;
  };
}

interface IRecordFormProps {
  t: (key: string, options?: any) => string;
  llmProfiles: ILLMProfile[];
  userType: string;
  userId: string | null;
  JWT: string | null;
  userRemainingCredit: number;
  formData: IRecordFormData;
  records: IRecord[];
  updateRecordFieldAction: typeof updateRecordFieldAction;
  setRecordFormErrorsAction: typeof setRecordFormErrorsAction;
  resetRecordFormAction: typeof resetRecordFormAction;
  prepareCreateRecordCommandAction: typeof prepareCreateRecordCommandAction;
  prepareUpdateRecordCommandAction: typeof prepareUpdateRecordCommandAction;
  addRecordAction: typeof addRecordAction;
  deleteRecordAndReadNextAction: typeof deleteRecordAndReadNextAction;
  setRecordStateAction: typeof setRecordStateAction;
  setViewModeAction: typeof setViewModeAction;
  showAllLinkable: boolean;
  searchText: string;
  setShowAllLinkableAction: typeof setShowAllLinkableAction;
  resetRecordsAction: typeof resetRecordsAction;
  prepareReadRecordsCommandAction: typeof prepareReadRecordsCommandAction;
  clearSummariesAction: typeof summariesActions.clearSummaries;
  latestPublicRecords: ILatestPublicRecord[];
  isInitialLoadInProgress: boolean;
}

const mapStateToProps = (state: IAppState) => {
  const currentSaasTenant = state.saasTenants.saasTenants.find((tenant) => tenant.saas_tenant_id === state.auth.userId);
  return {
    formData: state.records.recordForm,
    records: state.records.records,
    showAllLinkable: state.records.showAllLinkable,
    latestPublicRecords: state.records.latestPublicRecords,
    searchText: state.records.searchText,
    llmProfiles: state.llmProfiles.llmProfiles,
    userType: state.auth.userType,
    userId: state.auth.userId,
    JWT: state.auth.JWT,
    userRemainingCredit: (currentSaasTenant && (currentSaasTenant.credit as number) - ((currentSaasTenant.usage?.total_cost as number) || 0)) || 0,
    isInitialLoadInProgress: state.records.isInitialLoadInProgress,
  };
};

const mapDispatchToProps = (dispatch: Dispatch) =>
  bindActionCreators(
    {
      updateRecordFieldAction,
      setRecordFormErrorsAction,
      resetRecordFormAction,
      prepareCreateRecordCommandAction,
      prepareUpdateRecordCommandAction,
      addRecordAction,
      deleteRecordAndReadNextAction,
      setRecordStateAction,
      setViewModeAction,
      setShowAllLinkableAction,
      resetRecordsAction,
      prepareReadRecordsCommandAction,
      clearSummariesAction: summariesActions.clearSummaries,
    },
    dispatch,
  );

export const RecordForm = connect(mapStateToProps, mapDispatchToProps)(withTranslation()(RecordFormBase));
